import { type FormEvent, useState } from "react";
import { Button, ErrorNote, Field, Input, StatusBadge } from "@/components/ui";
import { errorMessage, useInvalidating, useMe, useWebsites } from "@/lib/api";
import { useCompleteStep } from "./useCompleteStep";

export function WebsiteStep() {
  const me = useMe();
  const websites = useWebsites();
  const complete = useCompleteStep();
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  const create = useInvalidating(
    (api, body: { url: string; organizationName?: string }) =>
      api.POST("/websites", { body }),
    [["websites"], ["me"]],
  );
  const remove = useInvalidating(
    (api, websiteId: string) =>
      api.DELETE("/websites/{websiteId}", { params: { path: { websiteId } } }),
    [["websites"]],
  );

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        url,
        ...(name.trim() && (websites.data?.length ?? 0) === 0
          ? { organizationName: name.trim() }
          : {}),
      });
      setUrl("");
    } catch (err) {
      setError(errorMessage(err, "Could not add that website."));
    }
  };

  const orgName = me.data?.organization.name ?? "";
  const sites = websites.data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-[24px] font-bold tracking-[-0.02em]">
          Where should the assistant learn from?
        </h1>
        <p className="mt-1 text-[14px] text-muted">
          We read your website so the assistant can answer with what is already
          on it. Add more than one if you have them.
        </p>
      </div>
      <form
        onSubmit={add}
        className="flex flex-col gap-4 rounded-panel border border-border bg-surface p-6"
      >
        {sites.length === 0 ? (
          <Field label="Company name">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={orgName}
            />
          </Field>
        ) : null}
        <Field
          label="Website"
          hint="Anything you would type in a browser: acme.com, https://www.acme.com/about"
        >
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="acme.com"
            required
          />
        </Field>
        <ErrorNote message={error} />
        <Button
          type="submit"
          variant="primary"
          disabled={create.isPending}
          className="self-start"
        >
          {create.isPending
            ? "Adding…"
            : sites.length === 0
              ? "Add website"
              : "Add another"}
        </Button>
      </form>
      {sites.length > 0 ? (
        <ul className="flex flex-col divide-y divide-border rounded-panel border border-border bg-surface">
          {sites.map((w) => (
            <li
              key={w.id}
              className="flex items-center justify-between gap-3 px-5 py-3"
            >
              <div className="min-w-0">
                <p className="truncate font-medium">{w.origin}</p>
                <p className="text-[12px] text-muted">
                  {w.sitemapStatus === "ready"
                    ? `${w.pageCount} pages found (${w.sitemapSource})`
                    : w.sitemapStatus === "failed"
                      ? (w.sitemapError ?? "Could not read the site")
                      : "Looking for pages…"}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={w.sitemapStatus} />
                <button
                  type="button"
                  className="text-[12px] text-muted hover:text-danger"
                  onClick={() => remove.mutate(w.id)}
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-muted">
          {sites.length === 0
            ? "Add at least one website to continue."
            : "Pages keep loading in the background."}
        </p>
        <Button
          variant="primary"
          disabled={sites.length === 0 || complete.isPending}
          onClick={() => complete.mutate("website")}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
